import {
  computed,
  DestroyRef,
  inject,
  Injectable,
  signal,
  untracked,
} from '@angular/core';
import { updateAnalyticsConsent } from './google-analytics';
import {
  COOKIE_CONSENT_STORAGE_KEY,
  readStoredConsent,
} from './cookie-consent.constants';

/** Prefixes of the cookies gtag.js writes — dropped when consent is revoked. */
const ANALYTICS_COOKIE_PREFIXES = ['_ga', '_gid', '_gat'];

/** Number of trailing labels forming the registrable domain (`panoris.com`). */
const REGISTRABLE_DOMAIN_LABELS = 2;

/**
 * Single source of truth for the analytics cookie consent.
 *
 * The answer lives in localStorage (one decision per browser) and is mirrored
 * into a signal so the UI can react to it. Every write also forwards the new
 * state to Google Consent Mode v2 and, on revocation, deletes the analytics
 * cookies that were already stored.
 *
 * A decision taken in one tab (or in the popped-out player window) is picked up
 * by the others through the `storage` event, so revoking never leaves a stale
 * tab reporting to GA.
 */
@Injectable({ providedIn: 'root' })
export class CookieConsentService {
  /** `null` while the user has not answered the consent modal yet. */
  private readonly state = signal<boolean | null>(readStoredConsent());

  readonly consent = this.state.asReadonly();
  readonly isGranted = computed(() => this.state() === true);
  readonly isAnswered = computed(() => this.state() !== null);

  constructor() {
    window.addEventListener('storage', this.syncFromStorage);
    inject(DestroyRef).onDestroy(() =>
      window.removeEventListener('storage', this.syncFromStorage),
    );
  }

  /**
   * Re-applies an already granted consent to gtag. Called once GA has been
   * bootstrapped, since Consent Mode defaults everything to "denied".
   *
   * Reads the state untracked: callers run it inside an `effect`, and this must
   * not subscribe that effect to consent changes.
   */
  applyStoredConsent(): void {
    if (untracked(this.state)) {
      updateAnalyticsConsent(true);
    }
  }

  grant(): void {
    this.set(true);
  }

  /** Withdraws a previously given consent and clears the analytics cookies. */
  revoke(): void {
    this.set(false);
  }

  set(granted: boolean): void {
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, String(granted));
    } catch {
      // Storage blocked — the decision still applies for this session.
    }

    this.state.set(granted);
    updateAnalyticsConsent(granted);

    if (!granted) {
      this.clearAnalyticsCookies();
    }
  }

  /** Adopts a decision made in another tab or window of the same origin. */
  private readonly syncFromStorage = (event: StorageEvent): void => {
    // A null key means the whole storage was cleared.
    if (event.key !== null && event.key !== COOKIE_CONSENT_STORAGE_KEY) {
      return;
    }

    const stored = readStoredConsent();
    if (stored === untracked(this.state)) {
      return;
    }

    this.state.set(stored);
    updateAnalyticsConsent(stored === true);
  };

  /**
   * Expires the analytics cookies on every domain scope they could have been
   * written for (host, host with leading dot, registrable domain), because the
   * scope used by gtag.js is not readable from `document.cookie`.
   */
  private clearAnalyticsCookies(): void {
    const { hostname } = window.location;
    const registrableDomain = hostname
      .split('.')
      .slice(-REGISTRABLE_DOMAIN_LABELS)
      .join('.');
    const domains = [
      undefined,
      hostname,
      `.${hostname}`,
      `.${registrableDomain}`,
    ];

    const names = document.cookie
      .split(';')
      .map(cookie => cookie.split('=')[0]?.trim())
      .filter(
        (name): name is string =>
          !!name &&
          ANALYTICS_COOKIE_PREFIXES.some(prefix => name.startsWith(prefix)),
      );

    for (const name of names) {
      for (const domain of domains) {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT${
          domain ? `; domain=${domain}` : ''
        }`;
      }
    }
  }
}
